import React from 'react';

import type { Media as MediaType } from '../../../payload/payload-types';
import { TextGalleryProps } from '@/app/_blocks/TextGalleryGraphQL/types';
import { Media } from '../Media';
import type { ImageSizeValue } from '../Media/types';
import { Button } from '../Button';
import { renderTrademarkText } from '../TrademarkText';

import classes from './TextGallery.module.scss';
import { DebugJsonBlock } from '../DebugJsonBlock';
import { Gutter } from '../Gutter';

type ButtonType = TextGalleryProps['buttons'][number]['button'];
interface GalleryButton extends ButtonType {
  textGalleryButtonStyle: ButtonType['style'];
  textGalleryButtonType: ButtonType['type'];
}

export const TextGallery: React.FC<{
  className?: string;
  textGallery: TextGalleryProps;
}> = ({ className, textGallery }) => {
  const { buttons = [], images = [] } = textGallery;
  const imageCount = images.length;
  const reversedClass = textGallery.reversed ? classes.reversed : '';
  const hasVisibleButtons = buttons.some(({ button }: { button: GalleryButton }) => !button.hideButton);
  return (
    <Gutter>
      <div
        className={[className, classes.wrapper, 'grid', reversedClass].filter(Boolean).join(' ')}
      >
        <div className={[`col-6 col-sm-12`, classes.leftContent, textGallery.textBackground && classes.textBackground].filter(Boolean).join(' ')}>
          {(() => {
            const headlineText = textGallery.title?.text || textGallery.title_legacy;
            const headlineTag = textGallery.title?.tag || 'h2';
            const HeadlineTag = headlineTag as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
            return headlineText ? (
              <HeadlineTag className={`${classes.heading} heading-h2`}>
                {renderTrademarkText(headlineText)}
              </HeadlineTag>
            ) : null;
          })()}
          {textGallery.description &&
            textGallery.description
              .split(/\r?\n/)
              .filter(paragraph => paragraph.trim() !== '')
              .map((paragraph, idx) => (
                <p key={idx} className={classes.paragraph}>
                  {renderTrademarkText(paragraph)}
                </p>
              ))}
          {hasVisibleButtons && (
            <div className={classes.buttonRow}>
              {buttons.map(({ button }: { button: GalleryButton }) =>
                button.hideButton ? null : (
                  <Button
                    key={button.label}
                    type={button.textGalleryButtonType || 'reference'}
                    appearance={button.textGalleryButtonStyle}
                    href={button.textGalleryButtonType === 'custom' ? button?.url : undefined}
                    reference={button.textGalleryButtonType === 'reference' ? button?.reference : undefined}
                    document={
                      button.textGalleryButtonType === 'download' &&
                      button.document &&
                      typeof button.document === 'object'
                        ? button.document
                        : undefined
                    }
                    label={button.label}
                    newTab={button.newTab}
                    width="45%"
                  />
                ),
              )}
            </div>
          )}
        </div>
        <div className="col-6 col-sm-12 grid">
          {images.map((item, index) => {
            const columnClass = index === 0 && !!(imageCount % 2) ? 'col-12' : 'col-6';
            const widthClass = index === 0 && imageCount === 3 ? classes.doubleWide : '';
            let imageSize: ImageSizeValue =
              index === 0 && imageCount === 3 ? 'landscape' : 'square';
            if (imageCount === 2) {
              imageSize = 'portrait';
            }
            const image = item.image as unknown as MediaType;
            return (
              <div className={`${columnClass} ${widthClass}`} style={{ position: 'relative' }} key={item.id}>
                <Media
                  key={item.id}
                  resource={image}
                  {...image}
                  className={classes.image}
                  imageSize={imageSize}
                />
                {(() => {
                  const labelText = item.label?.text || item.label_legacy;
                  const labelTag = item.label?.tag || 'span';
                  const LabelTag = labelTag as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span' | 'p' | 'div';

                  const secondaryLabelText =
                    item.secondaryLabel?.text || item.secondaryLabel_legacy;
                  const secondaryLabelTag = item.secondaryLabel?.tag || 'span';
                  const SecondaryLabelTag = secondaryLabelTag as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span' | 'p' | 'div';

                  if (!labelText && !secondaryLabelText) return null;

                  return (
                    <div className={classes.itemCardLabelWrapper}>
                      {labelText && (
                        <LabelTag className={classes.itemCardLabel}>
                          {renderTrademarkText(labelText)}
                        </LabelTag>
                      )}
                      {secondaryLabelText && (
                        <SecondaryLabelTag className={classes.itemCardSecondaryLabel}>
                          {renderTrademarkText(secondaryLabelText)}
                        </SecondaryLabelTag>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
        <DebugJsonBlock block={{ ...textGallery }} />
      </div>
    </Gutter>
  );
};

export default TextGallery;
